import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';

// ---- theme (follows the OS) ------------------------------------------------------

const mq = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
export function useOsTheme(): 'light' | 'dark' {
  return useSyncExternalStore(
    (cb) => {
      mq?.addEventListener('change', cb);
      return () => mq?.removeEventListener('change', cb);
    },
    () => (mq?.matches ? 'dark' : 'light'),
  );
}

// ---- toasts -----------------------------------------------------------------------

type ToastItem = { id: number; text: string; kind: 'info' | 'error' };
let toasts: ToastItem[] = [];
const listeners = new Set<() => void>();
let nextId = 1;
export function toast(text: string, kind: 'info' | 'error' = 'info'): void {
  const id = nextId++;
  toasts = [...toasts, { id, text, kind }];
  listeners.forEach((l) => l());
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    listeners.forEach((l) => l());
  }, kind === 'error' ? 6000 : 2800);
}
export function Toasts() {
  const list = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => toasts,
  );
  return (
    <div className="xb-toasts" role="status" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={`xb-toast xb-toast-${t.kind}`}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

// ---- modal ------------------------------------------------------------------------

export function Modal({ title, onClose, children, wide }: { title: string; onClose(): void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="xb-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`xb-modal${wide ? ' xb-modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="xb-modal-head">
          <h2>{title}</h2>
          <button className="xb-icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---- avatars ------------------------------------------------------------------------

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)} d ago`;
  return new Date(t).toLocaleDateString();
}

export function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

/** Excalidraw's own collaborator colour (getClientColor) — so header avatars match the canvas cursors. */
export function excalidrawClientColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash << 5) - hash + id.charCodeAt(i);
  return `hsl(${(Math.abs(hash) % 37) * 10}, 100%, 83%)`;
}
